import React, { useState, useEffect } from "react";
import {
  Search,
  Eye,
  Edit2,
  Trash2,
  Plus,
  Filter,
  Package,
  Layers,
  Monitor,
  ChevronDown,
  RefreshCw,
  MoreVertical,
} from "lucide-react";
import {
  offeringsService,
  adminAddonService,
  adminPlanService,
} from "../../../services/api/settings";
import {
  OfferingPlan,
  OfferingAddon,
  OfferingsResponse,
} from "../../../store/types/settings";
import Skeleton from "../../../components/elements/Skeleton";
import { toast } from "sonner";
import ViewAddonModal from "./misc/ViewAddonModal";
import ViewPlanModal from "./misc/ViewPlanModal";
import EditAddonModal from "./misc/EditAddonModal";
import EditPlanModal from "./misc/EditPlanModal";
import DeleteConfirmationModal from "./misc/DeleteConfirmationModal";
import formatPriceString from "../../../utility/formatPriceString";
import formatPrice from "../../../utility/formatPrice";
import LifecycleTables from "./misc/LifecycleTables";

type TabType = "ALL" | "RECRUITER" | "DIRECT" | "CONSOLE" | "LIFECYCLE";

const OfferingsDashboard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<OfferingsResponse["data"] | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<TabType>("ALL");
  const [selectedOffer, setSelectedOffer] = useState<any>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isViewPlanModalOpen, setIsViewPlanModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditPlanModalOpen, setIsEditPlanModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchOfferings = async () => {
    setLoading(true);
    try {
      const response = await offeringsService.getAll();
      if (response.status === "success") {
        setData(response.data);
      }
    } catch (error) {
      console.error("Error fetching offerings:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOfferings();
  }, []);

  const handleDelete = (item: any) => {
    setSelectedOffer(item);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedOffer) return;

    setDeleteLoading(true);
    try {
      const isPlan = selectedOffer.offerType === "PLAN";
      if (isPlan) {
        await adminPlanService.delete(selectedOffer.id);
      } else {
        await adminAddonService.delete(
          selectedOffer.id,
          selectedOffer.serviceType,
        );
      }
      setIsDeleteModalOpen(false);
      fetchOfferings(); // Refresh data
    } catch (error) {
      console.error("Delete failed:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleEdit = (offer: any) => {
    setSelectedOffer(offer);
    if (offer.offerType === "PLAN") {
      setIsEditPlanModalOpen(true);
    } else {
      setIsEditModalOpen(true);
    }
  };

  const handleView = (offer: any) => {
    setSelectedOffer(offer);
    if (offer.offerType === "PLAN") {
      setIsViewPlanModalOpen(true);
    } else {
      setIsViewModalOpen(true);
    }
  };

  const getAllItems = () => {
    if (!data) return [];

    const items: any[] = [];

    const processCategory = (cat: string, catData: any) => {
      catData.plans.forEach((p: OfferingPlan) =>
        items.push({ ...p, offerType: "PLAN", serviceType: cat }),
      );
      catData.addons.forEach((a: OfferingAddon) =>
        items.push({ ...a, offerType: "ADDON", serviceType: cat }),
      );
    };

    if (activeTab === "ALL" || activeTab === "CONSOLE")
      processCategory("CONSOLE", data.CONSOLE);
    if (activeTab === "ALL" || activeTab === "DIRECT")
      processCategory("DIRECT", data.DIRECT);
    if (activeTab === "ALL" || activeTab === "RECRUITER")
      processCategory("RECRUITER", data.RECRUITER);

    return items
      .filter(
        (item) =>
          (item.title || item.name || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (item.internalName || "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase()),
      )
      .reverse();
  };

  const filteredItems = getAllItems();

  return (
    <div className="p-6 bg-[#F8FAFC] min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Services and Features Management
            </h1>
            <p className="text-gray-500 mt-1">
              Manage your service plans and add-ons across all platforms.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchOfferings}
              className="p-2.5 text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors shadow-sm"
              title="Refresh data"
            >
              <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
            </button>
            <a
              href="/admin/addons/create"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-medium"
            >
              <Plus size={20} />
              <span>Create Add-on</span>
            </a>
            <a
              href="/admin/plans/create"
              className="flex items-center gap-2 px-5 py-2.5 bg-primary text-white rounded-xl hover:bg-primary/90 transition-all shadow-md shadow-primary/20 font-medium"
            >
              <Plus size={20} />
              <span>Create Plan</span>
            </a>
          </div>
        </div>

        {/* Stats Summary */}
        {!loading && data && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                  <Package size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium capitalize">
                    RECRUITER
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {data.RECRUITER.plans.length} Plans /{" "}
                    {data.RECRUITER.addons.length} Add-ons
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-50 text-purple-600 rounded-xl">
                  <Layers size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium capitalize">
                    DIRECT
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {data.DIRECT.plans.length} Plans /{" "}
                    {data.DIRECT.addons.length} Add-ons
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-xl">
                  <Monitor size={24} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 font-medium capitalize">
                    CONSOLE
                  </p>
                  <p className="text-xl font-bold text-gray-900">
                    {data.CONSOLE.plans.length} Plans /{" "}
                    {data.CONSOLE.addons.length} Add-ons
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm mb-4 flex flex-col lg:flex-row items-center justify-between gap-4">
          <div className="flex items-center p-1 bg-gray-50 rounded-xl w-full lg:w-auto overflow-x-auto">
            {(["ALL", "RECRUITER", "DIRECT", "CONSOLE", "LIFECYCLE"] as TabType[]).map(
              (tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-6 py-2 rounded-lg text-sm font-semibold transition-all whitespace-nowrap ${
                    activeTab === tab
                      ? "bg-white text-primary shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ),
            )}
          </div>

          <div className="relative w-full lg:w-96">
            <Search
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search by name or ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 border-none rounded-xl text-sm focus:ring-2 focus:ring-primary/20 transition-all outline-none"
            />
          </div>
        </div>

        {/* Table / Lifecycle content */}
        {activeTab === "LIFECYCLE" && data?.LIFECYCLE ? (
          <LifecycleTables data={data.LIFECYCLE} refresh={fetchOfferings} />
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50/50 border-b border-gray-100">
                  <th className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Offering
                  </th>
                  <th className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Service
                  </th>
                  <th className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider">
                    Pricing
                  </th>
                  <th className="px-6 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      <td className="px-6 py-2">
                        <Skeleton height={20} width={150} />
                      </td>
                      <td className="px-6 py-2">
                        <Skeleton height={20} width={80} />
                      </td>
                      <td className="px-6 py-2">
                        <Skeleton height={20} width={60} />
                      </td>
                      <td className="px-6 py-2">
                        <Skeleton height={20} width={100} />
                      </td>
                      <td className="px-6 py-2 text-right">
                        <Skeleton height={30} width={100} className="ml-auto" />
                      </td>
                    </tr>
                  ))
                ) : filteredItems.length > 0 ? (
                  filteredItems.map((item) => {
                    const isAddon = item.offerType === "ADDON";
                    const itemName = isAddon ? item.name : item.title;
                    const price = isAddon
                      ? typeof item.price === "string"
                        ? item.price
                        : `₦${item.price.toLocaleString()}`
                      : formatPriceString(item.monthlyPrice);
                    // : `₦${parseFloat(item.monthlyPrice).toLocaleString()}`;

                    return (
                      <tr
                        key={item.id}
                        className="hover:bg-gray-50/50 transition-colors group"
                      >
                        <td className="px-6 py-2">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                              {itemName}
                            </span>
                            <span className="text-xs text-gray-400 mt-0.5 font-mono">
                              {item.id}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${
                              item.serviceType === "RECRUITER"
                                ? "bg-blue-50 text-blue-700"
                                : item.serviceType === "DIRECT"
                                  ? "bg-purple-50 text-purple-700"
                                  : "bg-emerald-50 text-emerald-700"
                            }`}
                          >
                            {item.serviceType}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <span
                            className={`text-xs font-semibold ${isAddon ? "text-orange-600" : "text-indigo-600"}`}
                          >
                            {item.offerType}
                          </span>
                        </td>
                        <td className="px-6 py-2">
                          <div className="flex flex-col">
                            <span className="text-sm font-bold text-gray-900">
                              {price}
                              {/* {formatPriceString(price || "0")} */}
                            </span>
                            <span className="text-xs text-gray-400 font-medium">
                              /{item.billingType || "Month"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-2 text-right">
                          <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => handleView(item)}
                              className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all"
                              title="View Details"
                            >
                              <Eye size={18} />
                            </button>
                            <button
                              onClick={() => handleEdit(item)}
                              className="p-2 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all"
                              title="Edit Offering"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDelete(item)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"
                              title="Delete Offering"
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                          <div className="group-hover:hidden text-gray-300">
                            <MoreVertical size={18} className="ml-auto" />
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="p-4 bg-gray-50 rounded-full text-gray-300 mb-3">
                          <Search size={32} />
                        </div>
                        <p className="text-gray-500 font-medium">
                          No offerings found matching your search
                        </p>
                        <button
                          onClick={() => {
                            setSearchQuery("");
                            setActiveTab("ALL");
                          }}
                          className="text-primary text-sm font-bold mt-2 hover:underline"
                        >
                          Clear all filters
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {/* Modals */}
      {isViewModalOpen && selectedOffer && (
        <ViewAddonModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          data={selectedOffer}
        />
      )}

      {isViewPlanModalOpen && selectedOffer && (
        <ViewPlanModal
          isOpen={isViewPlanModalOpen}
          onClose={() => setIsViewPlanModalOpen(false)}
          plan={selectedOffer}
        />
      )}

      {isEditModalOpen && selectedOffer && (
        <EditAddonModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          data={selectedOffer}
          onSuccess={fetchOfferings}
        />
      )}

      {isEditPlanModalOpen && selectedOffer && (
        <EditPlanModal
          isOpen={isEditPlanModalOpen}
          onClose={() => setIsEditPlanModalOpen(false)}
          data={selectedOffer}
          onSuccess={fetchOfferings}
        />
      )}

      {isDeleteModalOpen && selectedOffer && (
        <DeleteConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Offering"
          itemName={selectedOffer.name || selectedOffer.title}
          loading={deleteLoading}
        />
      )}
    </div>
  );
};

export default OfferingsDashboard;
